<template>
  <form class="contract-editor" @submit.prevent="submit">
    <div class="contract-editor__grid">
      <div class="contract-editor__field contract-editor__field--full">
        <label class="contract-editor__label" for="contract-concept">Concepto</label>
        <input
          id="contract-concept"
          v-model.trim="form.concept"
          class="contract-editor__input"
          type="text"
          placeholder="Descripcion del contrato"
        >
      </div>

      <div class="contract-editor__field">
        <label class="contract-editor__label" for="contract-value">Monto</label>
        <input
          id="contract-value"
          v-model.number="form.value"
          class="contract-editor__input"
          type="number"
          min="0"
          step="0.01"
          placeholder="0"
        >
      </div>

      <div class="contract-editor__field">
        <label class="contract-editor__label" for="contract-currency">Moneda</label>
        <select
          id="contract-currency"
          v-model="form.currency_id"
          class="contract-editor__input"
        >
          <option disabled value="0">Seleccione una moneda</option>
          <option v-for="currency in currencies" :key="currency.id" :value="currency.id">
            {{ currency.description }}
          </option>
        </select>
      </div>
    </div>

    <p v-if="error" class="contract-editor__error">{{ error }}</p>

    <footer class="contract-editor__actions">
      <button class="contract-editor__ghost" type="button" :disabled="saving" @click="$emit('cancel')">
        Cancelar
      </button>

      <button class="contract-editor__primary" type="submit" :disabled="saving">
        {{ saving ? 'Guardando...' : submitLabel }}
      </button>
    </footer>
  </form>
</template>

<script>
import companyOperationsService from '@/services/company/companyOperations.service'

const defaultForm = () => ({
  id: null,
  concept: '',
  value: '',
  currency_id: 0
})

export default {
  name: 'ContractEditor',
  props: {
    value: {
      type: Object,
      default: null
    },
    companyId: {
      type: [String, Number],
      required: true
    },
    currencies: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      form: defaultForm(),
      saving: false,
      error: ''
    }
  },
  computed: {
    submitLabel () {
      return this.form.id ? 'Guardar cambios' : 'Crear contrato'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        this.form = {
          ...defaultForm(),
          ...(newValue || {})
        }
        if (!this.form.currency_id) {
          this.form.currency_id = 0
        }
      }
    }
  },
  methods: {
    validate () {
      if (!this.form.concept) {
        this.error = 'El concepto es obligatorio.'
        return false
      }
      if (this.form.value === null || this.form.value === '' || Number.isNaN(Number(this.form.value))) {
        this.error = 'El monto es obligatorio.'
        return false
      }
      if (!this.form.currency_id || Number(this.form.currency_id) === 0) {
        this.error = 'Debes seleccionar una moneda.'
        return false
      }
      this.error = ''
      return true
    },
    normalizePayload () {
      return {
        id: this.form.id,
        company_id: Number(this.companyId),
        concept: this.form.concept,
        value: Number(this.form.value),
        currency_id: Number(this.form.currency_id)
      }
    },
    async submit () {
      if (!this.validate()) return

      this.saving = true
      this.error = ''

      try {
        const payload = this.normalizePayload()
        const saved = this.form.id
          ? await companyOperationsService.updateContract(payload)
          : await companyOperationsService.createContract(payload)

        this.$emit('saved', saved)
      } catch (err) {
        this.error = err?.message || 'No fue posible guardar el contrato.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.contract-editor {
  display: grid;
  gap: 1rem;
}

.contract-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.contract-editor__field {
  display: grid;
  gap: 0.38rem;
}

.contract-editor__field--full {
  grid-column: span 2;
}

.contract-editor__label {
  color: #4b3b2f;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.contract-editor__input {
  min-height: 2.9rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(102, 81, 57, 0.14);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.76);
  color: #241c16;
}

.contract-editor__error {
  margin: 0;
  color: #a24444;
  font-size: 0.9rem;
}

.contract-editor__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
}

.contract-editor__ghost,
.contract-editor__primary {
  min-height: 2.65rem;
  padding: 0 0.95rem;
  border-radius: 999px;
}

.contract-editor__ghost {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
}

.contract-editor__primary {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

@media (max-width: 640px) {
  .contract-editor__grid {
    grid-template-columns: 1fr;
  }

  .contract-editor__field--full {
    grid-column: span 1;
  }

  .contract-editor__actions {
    flex-direction: column;
  }
}
</style>
